import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CreditCard, BookOpen, CalendarDays, Church, ShieldCheck, Landmark } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CourseCheckout } from "@/components/CourseCheckout";
import { OneToOneCheckout } from "@/components/OneToOneCheckout";
import { BankTransfer } from "@/components/BankTransfer";
import { GospelUnlock } from "@/components/GospelCourse";
import { useAuth } from "@/context/AuthContext";

const JUMPS = [
  { id: "courses", label: "Courses", icon: BookOpen },
  { id: "one-to-one", label: "1:1 sessions", icon: CalendarDays },
  { id: "gospel", label: "Gospel courses", icon: Church },
  { id: "licences", label: "Class licences", icon: ShieldCheck },
  { id: "bank", label: "Bank transfer", icon: Landmark },
];

const Section = ({ id, eyebrow, title, children }) => (
  <section id={id} className="mt-16 scroll-mt-28" data-testid={`payment-section-${id}`}>
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">{eyebrow}</p>
    <h2 className="mt-2 font-serif text-3xl">{title}</h2>
    {children}
  </section>
);

export default function Payment() {
  const { user } = useAuth();
  const { hash } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => { if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }); }, 300);
    return () => clearTimeout(t);
  }, [hash]);
  const staff = user && (user.role === "teacher" || user.role === "admin");
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8 lg:py-14" data-testid="payment-page">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Secure checkout</p>
        <h1 className="mt-3 flex items-center gap-3 font-serif text-4xl tracking-tight sm:text-5xl"><CreditCard className="text-primary" size={40} /> Payment</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">Pay for a course, a bundle, 1:1 sessions, a Gospel course or a class licence, all in one place. Card, Apple Pay and Google Pay accepted.</p>
        <nav className="mt-6 flex flex-wrap gap-2" data-testid="payment-jumps">
          {JUMPS.map((j) => (
            <a key={j.id} href={`#${j.id}`} data-testid={`payment-jump-${j.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-primary hover:text-primary">
              <j.icon size={15} /> {j.label}
            </a>
          ))}
        </nav>
        <section id="courses" className="scroll-mt-28" data-testid="payment-section-courses"><CourseCheckout /></section>
        <Section id="one-to-one" eyebrow="Private tuition" title="1:1 sessions with Jason">
          <p className="mt-3 max-w-2xl text-muted-foreground"><span className="font-semibold text-foreground">£70 per hour</span>. Book as many hours as you like, then we'll arrange a time to suit you.</p>
          <div className="mt-5"><OneToOneCheckout /></div>
        </Section>
        <Section id="gospel" eyebrow="£300 each · any two for £500" title="Gospel courses">
          <p className="mt-3 max-w-2xl text-muted-foreground">Gospel Piano and Gospel Guitar, each at Beginner and Intermediate level (6 lessons each). Available once you've bought any standard course. Mix levels or instruments — any two for £500. Sold separately, not part of bundles.</p>
          <GospelUnlock />
        </Section>
        <Section id="licences" eyebrow="For schools & teachers" title="Class licences">
          <p className="mt-3 max-w-2xl text-muted-foreground">£400 per class per course, or £350 with a referral code. Licences are bought for a class from the teacher dashboard, so your students are added automatically.</p>
          <Link to={staff ? "/teacher" : "/class-teaching"} data-testid="payment-licence-btn"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
            <ShieldCheck size={15} /> {staff ? "Buy a licence for my class" : "Find out about Class Teaching"}
          </Link>
        </Section>
        <Section id="bank" eyebrow="Prefer not to pay by card?" title="Pay by bank transfer">
          <p className="mt-3 max-w-2xl text-muted-foreground">Send the amount by bank transfer, then tell Jason below. Your course unlocks once the payment is confirmed.</p>
          <BankTransfer />
        </Section>
      </main>
    </div>
  );
}
